import React, { FC, Fragment, useMemo } from 'react';
import { Waypoint } from 'react-waypoint';

import { getExploreSectionDataTestId, TEST_ID_FEATURED_START } from 'constants/aqa/explore';
import { CardSchema, DiscoverSectionSchema, EDiscoverSlotTypeOptions } from 'constants/graphqlTypes';
import { EFeaturedFlags } from 'types/EFeatureFlags';
import { EUiButtonType } from 'components/UI/Button/constants';

import Button from 'components/UI/Button';
import LoaderContent from 'components/UI/LoaderContent/LoaderContent';
import OnboardingAlert from 'components/UI/OnboardingAlert';
import InviteBlock from 'components/InviteBlock';
import Collection from 'components/Collection';
import UserCard from 'components/Card/UserCard';
import Card from 'components/Card';
import SparkCard from 'components/Sparks/Views/SparkCard';
import UniversalSlider from 'components/Sliders/UniversalSlider';

import useTheme from 'helpers/useTheme';
import getAuthUser from 'helpers/getAuthUser';
import { useFeaturedFlagEnabled } from 'helpers/posthogHooks';
import { useRefreshDetection } from 'helpers/useRefreshDetection';

import { useExploreItems } from 'graphQL/cards/exploreCards/hooks';
import { useGetFeaturedSparkCards } from 'graphQL/sparks/hooks';
import {
  StyledBlockWrap,
  StyledContent,
  StyledFeedbackWrap,
  StyledGuestWrap,
  StyledSection,
  StyledSparkItem,
} from './styled';

import Item from './Item';
import Footer from './Footer';
import Heading from './Heading';
import AboutUsSection from './AboutUs';
import GuestSection from './GuestSection';
import EditorialSection from './EditorialSection';
import TopicList from '../TopicsList/TopicList';
import DynamicListSection from './DynamicList';
import ListSection from './ListSection';

const {
  AboutUs,
  Auto,
  Manual,
  GuestListSeries,
  DynamicList,
  EditorialNote,
  SuggestedTopics,
  SuggestedUsers,
  Carousel,
  Spark,
  List,
  FeaturedLists,
} = EDiscoverSlotTypeOptions;

const ExploreTab: FC = () => {
  const isViewSparks = useFeaturedFlagEnabled(EFeaturedFlags.ViewSparks);
  const isDark = useTheme();
  const { username } = getAuthUser();
  const hasRefreshed = useRefreshDetection('Featured');

  const { sections, loading, total, onWaypointEnter } = useExploreItems();
  const { items: featuredSparkItems } = useGetFeaturedSparkCards({ limit: 20 });

  // Define a union type for feed items: either a section or an individual spark card
  type FeedItem = { type: 'section'; section: DiscoverSectionSchema } | { type: 'spark'; spark: CardSchema };

  // Merge admin-featured sparks into the feed as individual cards
  // Featured sparks are already sorted by featured_position, then by published_at/created_at from the backend
  const feedItems = useMemo((): FeedItem[] => {
    // Convert sections to feed items, excluding Spark sections (we'll inject individual sparks instead)
    const sectionItems: FeedItem[] = sections
      .filter((s) => s.type !== Spark) // Exclude SparkSection sliders
      .map((section) => ({ type: 'section' as const, section }));

    // Create individual feed items for featured sparks
    const sparkItems: FeedItem[] = featuredSparkItems.map((spark) => ({
      type: 'spark' as const,
      spark,
    }));

    // Merge sparks at the beginning of the feed (they're already sorted by position/recency)
    return [...sparkItems, ...sectionItems];
  }, [sections, featuredSparkItems]);

  return (
    <>
      {feedItems.map((feedItem, index) => {
        const itemKey = feedItem.type === 'spark' ? `spark-${feedItem.spark.id}` : feedItem.section.id;

        return (
          <Fragment key={itemKey}>
            {(feedItems.length === 1 || index === feedItems.length - 4 || index === feedItems.length - 1) && (
              <Waypoint onEnter={onWaypointEnter} />
            )}

            {/* Render individual spark cards - same pattern as Following feed */}
            {feedItem.type === 'spark' && isViewSparks && (
              <StyledSparkItem data-testid="featured-spark-item">
                <SparkCard
                  isDark={isDark}
                  card={feedItem.spark}
                  minimal
                  isOtherUser
                  expandedHeader
                  showFooterMenu
                  isFirstItem={index === 0}
                />
              </StyledSparkItem>
            )}

            {/* Render sections */}
            {feedItem.type === 'section' &&
              (() => {
                const { section } = feedItem;
                const shouldHide = (section.type === Auto || section.type === Manual) && !section.cardItems?.length;
                if (shouldHide) return null;

                return (
                  <StyledSection data-testid={section.type && getExploreSectionDataTestId(section.type, 'section')}>
                    {section.type === GuestListSeries && (
                      <StyledGuestWrap hasOneItem={section.cardItems?.length === 1}>
                        <GuestSection
                          title={hasRefreshed ? section.title ?? '' : ''}
                          description={section.annotation ?? ''}
                          cards={section.cardItems?.map(({ card }) => card) ?? []}
                        />
                      </StyledGuestWrap>
                    )}
                    {section.type === AboutUs && <AboutUsSection />}
                    {section.type === EditorialNote && <EditorialSection section={section} />}
                    {section.type === SuggestedTopics && section.suggestedTopics && (
                      <TopicList
                        topicListId={section.id}
                        tags={section.suggestedTopics}
                        title={hasRefreshed ? section.title : null}
                      />
                    )}
                    {section.type === DynamicList && (
                      <DynamicListSection
                        section={hasRefreshed ? section : { ...section, title: null }}
                        isDark={isDark}
                      />
                    )}
                    {(section.type === Auto || section.type === Manual) && (
                      <>
                        {hasRefreshed && <Heading section={section} />}
                        <StyledContent>
                          {section.cardItems?.map(
                            (item) =>
                              item && (
                                <Item
                                  dataTestId={item.layout ? getExploreSectionDataTestId(item.layout, 'pick-item') : ''}
                                  key={item?.card.id}
                                  item={item}
                                />
                              )
                          )}
                        </StyledContent>
                      </>
                    )}
                    {section.type === List && section.cardItems?.[0]?.card && (
                      <ListSection key={section?.cardItems?.[0]?.card.id} item={section?.cardItems?.[0]?.card} />
                    )}
                    {section.type === SuggestedUsers && section.cardItems && (
                      <UniversalSlider
                        title={hasRefreshed ? section.title : null}
                        description={section.annotation}
                        items={section.cardItems.map((item) => ({ ...item, id: item.card.id }))}
                        isInfinite
                        itemRender={({ card }, isCurrent, imagesKey) =>
                          card.user && (
                            <UserCard
                              item={card.user}
                              enableOldBehaviour={false}
                              isFullHeight
                              imagesKey={imagesKey}
                              isCurrent={isCurrent}
                              hasAnimatedImages
                            />
                          )
                        }
                      />
                    )}
                    {section.type === FeaturedLists && section.cardItems && (
                      <UniversalSlider
                        title={hasRefreshed ? section.title : null}
                        description={section.annotation}
                        items={section.cardItems.map((item) => ({ ...item, id: item.card.id }))}
                        itemRender={({ card }) => (
                          <Collection
                            showCreators
                            showListDescription
                            hidePrivateLabel
                            hideCollaborators
                            hasSmallTitle
                            item={card}
                          />
                        )}
                      />
                    )}
                    {section.type === Carousel && section.cardItems && (
                      <UniversalSlider
                        title={hasRefreshed ? section.title : null}
                        description={section.annotation}
                        items={section.cardItems.map((item) => ({ ...item, id: item.card.id }))}
                        slidesPerView={2.25}
                        itemRender={({ card, highlight }) => (
                          <Card
                            isDark={isDark}
                            card={card}
                            highlight={highlight}
                            hideFooterAvatars
                            disableAnimation
                            hideActionsUserInfo
                            hideTopAvatars
                            isSmall
                            isCarousel
                          />
                        )}
                      />
                    )}
                  </StyledSection>
                );
              })()}

            {index === feedItems.length - 1 && <Waypoint onEnter={onWaypointEnter} />}
          </Fragment>
        );
      })}
      {loading && <LoaderContent isFullScreen={!feedItems.length} isDark={isDark} />}
      {!loading && feedItems.length >= total && (
        <>
          <StyledSection>
            <StyledFeedbackWrap>
              <OnboardingAlert
                title="Are you enjoying the featured picks?"
                isTransparent
                titleSize={22}
                isDark={isDark}
              >
                We would love your thoughts so we can make guide:human even better.
                <Button
                  dataTestId={`${TEST_ID_FEATURED_START}_send-feedback-button`}
                  handler={() => {
                    window.location.href = `mailto:hello@guidehuman.co?subject=User feedback :${username}`;
                  }}
                  text="Send us feedback"
                  type={EUiButtonType.Primary}
                  isDark={isDark}
                />
              </OnboardingAlert>
            </StyledFeedbackWrap>
          </StyledSection>
          <Footer />
          <StyledBlockWrap>
            <InviteBlock isDark={isDark} />
          </StyledBlockWrap>
        </>
      )}
    </>
  );
};

export default ExploreTab;
